"use client"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import {useTracking} from "@/app/contexts/tracking-context"
import {isPartnerLink} from "@/app/utils/partner-link"
import {IAppliedFilter, IButtonConfig, IColumnConfig} from "@/app/models/auctor"
import {evaluateExpressionWithFiltersAndRowData, getClientQueryParams} from "@/app/utils/expression-utils"
import dynamic from "next/dynamic"
import {useCallback} from "react"

const AppIcon = dynamic(() => import("@/app/components/atomic/AppIcon"), {
    ssr: false,
})

interface IResultButtonProps {
    column: IColumnConfig
    resultRow: any
    appliedFilters: IAppliedFilter[]
    queryParams?: Record<string, string> | null
}

export default function ResultButton({column, resultRow, appliedFilters, queryParams}: IResultButtonProps) {
    const {track} = useTracking()

    const finalQueryParams = queryParams ?? getClientQueryParams()

    const buildUrlWithEvaluatedParams = useCallback(
        (baseUrl: string | null, buttonConfig?: IButtonConfig | null) => {
            if (!baseUrl) return baseUrl
            if (!buttonConfig || !buttonConfig.url_params || buttonConfig.url_params.length === 0) {
                return baseUrl
            }

            let urlObj: URL | null = null
            try {
                urlObj = new URL(baseUrl)
            } catch {
                try {
                    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000"
                    urlObj = new URL(baseUrl, origin)
                } catch {
                    return baseUrl
                }
            }

            for (const param of buttonConfig.url_params) {
                if (!param || !param.nameTargetColumn || !param.valueExpression) continue

                if (!resultRow[param.nameTargetColumn]) continue

                try {
                    const value = evaluateExpressionWithFiltersAndRowData(
                        param.valueExpression,
                        resultRow,
                        appliedFilters,
                        undefined,
                        finalQueryParams
                    )

                    if (value !== null && value !== undefined && String(value) !== "") {
                        urlObj.searchParams.set(resultRow[param.nameTargetColumn], String(value))
                    }
                } catch (error) {
                    console.warn("Failed to evaluate url param expression", param, error)
                }
            }

            return urlObj.toString()
        },
        [appliedFilters, finalQueryParams, resultRow]
    )

    const getButtonLink = useCallback(
        (columnConfig: IColumnConfig, resultRow: any) => {
            const buttonConfig = columnConfig.button_config as IButtonConfig
            if (buttonConfig && buttonConfig.has_conditional_url && buttonConfig.conditional_url_expression) {
                const expression = buttonConfig.conditional_url_expression

                const url = evaluateExpressionWithFiltersAndRowData(
                    expression,
                    resultRow,
                    appliedFilters,
                    undefined,
                    finalQueryParams
                )

                if (url) {
                    return buildUrlWithEvaluatedParams(String(url), buttonConfig)
                }
            }

            if (columnConfig.data_column_name === null) {
                return null
            }

            const base = resultRow[columnConfig.data_column_name]
            return buildUrlWithEvaluatedParams(base, buttonConfig)
        },
        [column, resultRow, appliedFilters, finalQueryParams, buildUrlWithEvaluatedParams]
    )

    if (!column.button_config) {
        return null
    }

    const shouldOpenInNewTab = (buttonConfig: IButtonConfig, resultRow: any) => {
        if (buttonConfig.target_self) {
            return false
        }

        if (buttonConfig.target_self_condition) {
            try {
                let expression = buttonConfig.target_self_condition
                if (!expression.includes("return")) {
                    expression = `return ${expression}`
                }

                const expressionFunction = new Function("data", expression)
                const expressionResult = expressionFunction(resultRow)

                return expressionResult !== true
            } catch (error) {
                console.error(`Error evaluating condition for button target blank ${buttonConfig.id}:`, error)
                return true
            }
        }

        return true
    }

    const buttonClicked = () => {
        if (!column.button_config) {
            return
        }

        if (column.button_config.gtm_event !== null) {
            track({
                event: column.button_config.gtm_event,
            })
        }

        const buttonLink = getButtonLink(column, resultRow)

        if (!buttonLink) {
            return
        }

        if (isPartnerLink(buttonLink)) {
            track({
                event: "gtm_partner_link_clic",
                partner_url: buttonLink,
                event_source: "auctor_comparator_result_button",
            })
        }

        const targetUrl: string = buttonLink!
        if (shouldOpenInNewTab(column.button_config, resultRow)) {
            window.open(targetUrl, "_blank")
        } else {
            window.location.href = targetUrl
        }
    }

    const buttonLink = getButtonLink(column, resultRow)
    const shouldRenderButton = column.button_config && buttonLink !== null && buttonLink !== ""

    if (shouldRenderButton) {
        const openInNewTab = shouldOpenInNewTab(column.button_config as IButtonConfig, resultRow)
        const useHref = !isPartnerLink(buttonLink ?? "")

        return (
            <AppButton
                theme="orange"
                arrowPosition="before"
                wFull
                href={useHref ? (buttonLink ?? undefined) : undefined}
                target={openInNewTab ? "_blank" : "_self"}
                onClick={() => buttonClicked()}
                dataTest="comparator-result-button"
            >
                {column.button_config.icon && <AppIcon iconClassName={`${column.button_config.icon}`}/>}
                {column.button_config.label}
            </AppButton>
        )
    }

    const shouldDisplayFallback =
        column.button_config.fallback_text !== null && column.button_config.fallback_text !== undefined

    if (shouldDisplayFallback) {
        if (!column.button_config.fallback_text) {
            return null
        }

        return (
            <div
                dangerouslySetInnerHTML={{
                    __html:
                        evaluateExpressionWithFiltersAndRowData(
                            column.button_config.fallback_text,
                            resultRow,
                            appliedFilters,
                            undefined,
                            finalQueryParams
                        ) ?? "",
                }}
            />
        )
    }

    return null
}
